/**
 * Physics-based spring presets for Framer Motion.
 *
 * SINGLE SOURCE OF TRUTH for all animation timing values.
 *
 * Damping ratio (ζ) = damping / (2 × √(stiffness × mass))
 *   ζ < 1  → underdamped (bouncy)
 *   ζ = 1  → critically damped (fastest settle, no bounce)
 *   ζ > 1  → overdamped (slower than critical!)
 */

import type { Transition } from "framer-motion";

/**
 * Entrance spring presets.
 *
 * | Preset   | ζ   | Use For                              |
 * |----------|-----|--------------------------------------|
 * | bounce   | 0.4 | Celebrations, milestones, onboarding |
 * | standard | 0.7 | Modals, sidebars, page transitions   |
 * | quick    | 0.8 | Hover states, toggles, buttons       |
 * | instant  | 0.9 | Collapsibles, dropdowns, tooltips    |
 */
export const springPresets = {
  bounce: { type: "spring", stiffness: 400, damping: 17, mass: 1 },
  standard: { type: "spring", stiffness: 500, damping: 31, mass: 1 },
  quick: { type: "spring", stiffness: 600, damping: 39, mass: 1 },
  instant: { type: "spring", stiffness: 700, damping: 47, mass: 1 },
} as const satisfies Record<string, Transition>;

export type SpringPreset = keyof typeof springPresets;

/** Exit presets — critically damped (ζ ≈ 1.0) for instant dismissals. */
export const exitPresets = {
  bounce: { type: "spring", stiffness: 500, damping: 45, mass: 1 },
  standard: { type: "spring", stiffness: 600, damping: 49, mass: 1 },
  quick: { type: "spring", stiffness: 700, damping: 53, mass: 1 },
  instant: { type: "spring", stiffness: 800, damping: 57, mass: 1 },
} as const satisfies Record<SpringPreset, Transition>;

/** Reduced motion fallbacks. */
export const reducedMotionPresets = {
  bounce: { duration: 0.15 },
  standard: { duration: 0.1 },
  quick: { duration: 0.07 },
  instant: { duration: 0.05 },
} as const satisfies Record<SpringPreset, Transition>;

/** Debug mode spring presets — ~4x slower for visual inspection. */
export const debugSpringPresets = {
  bounce: { type: "spring", stiffness: 100, damping: 8.5, mass: 1 },
  standard: { type: "spring", stiffness: 125, damping: 15.5, mass: 1 },
  quick: { type: "spring", stiffness: 150, damping: 19.5, mass: 1 },
  instant: { type: "spring", stiffness: 175, damping: 23.5, mass: 1 },
} as const satisfies Record<SpringPreset, Transition>;

/** Debug mode exit presets — ~4x slower for visual inspection. */
export const debugExitPresets = {
  bounce: { type: "spring", stiffness: 125, damping: 22.5, mass: 1 },
  standard: { type: "spring", stiffness: 150, damping: 24.5, mass: 1 },
  quick: { type: "spring", stiffness: 175, damping: 26.5, mass: 1 },
  instant: { type: "spring", stiffness: 200, damping: 28.5, mass: 1 },
} as const satisfies Record<SpringPreset, Transition>;
